import { Contract } from '@/DTOS/Contract/Response/Contract';
import ContractService from '@/services/contract/ContractService';
import { useQuery } from '@tanstack/react-query';
import { Card, Divider, Drawer, Spin, Table, Tag } from 'antd';
import moment from 'moment';
import React from 'react';

export interface ContractUpdateFieldRowDto {
  fieldName: string;
  before: string | null;
  after: string | null;
  isChanged: boolean;
}

export interface ContractUpdateWorkPlaceItemRowDto {
  workPlaceName: string;
  systemTypeName: string;
  periodicity: string;
  dateEnd: string | null;
  beforeWorkPlaceName: string | null;
  beforeSystemTypeName: string | null;
  beforePeriodicity: string | null;
  beforeDateEnd: string | null;
  afterWorkPlaceName: string | null;
  afterSystemTypeName: string | null;
  afterPeriodicity: string | null;
  afterDateEnd: string | null;
  isChanged: boolean;
}

export interface ContractUpdateRowDto {
  camp: string;
  inainte: string;
  dupa: string;
  isChanged: boolean;
}

export interface ContractUpdateDto {
  contractNumber: string;
  clientName: string;
  modifiedBy: string;
  modifiedWhen: string;
  operation: string;
  rows: ContractUpdateRowDto[];
  workPlaceGroupedRows: ContractUpdateRowDto[];
  contractFields: ContractUpdateFieldRowDto[];
  workPlaceItems: ContractUpdateWorkPlaceItemRowDto[];
}

interface Props {
  handleModalInformationClose: () => void;
  modalInformation: boolean;
  selectedRecord: Contract | null;
  selectedRecordAdditionalAct: Contract | null;
}

const ContractInformation: React.FC<Props> = ({
  handleModalInformationClose,
  modalInformation,
  selectedRecord,
  selectedRecordAdditionalAct,
}) => {
  const isSmallScreen = window.innerWidth <= 768;

  const { data: contractUpdatesResponse, isFetching: isUpdatesLoading } = useQuery({
    queryKey: ['contractUpdates', selectedRecord?.id],
    queryFn: () => ContractService.getContractUpdates(selectedRecord!.id),
    enabled: !!modalInformation && !!selectedRecord?.id,
  });
  const contractUpdate: ContractUpdateDto | null = contractUpdatesResponse?.response ?? null;
  // Helper to compare specific properties between records
  const compareSpecificFields = (oldRecord: Contract | null, newRecord: Contract | null) => {
    const comparisons: any[] = [];

    const fieldsToCompare = [
      { field: 'date', label: 'Date' },
      { field: 'dateStart', label: 'Start Date' },
      { field: 'dateEnd', label: 'End Date' },
    ];

    // Compare dates
    fieldsToCompare.forEach(({ field, label }) => {
      const oldValue = oldRecord ? moment(oldRecord[field as keyof Contract]).format('DD.MM.YYYY') : 'N/A';
      const newValue = newRecord ? moment(newRecord[field as keyof Contract]).format('DD.MM.YYYY') : 'N/A';
      if (oldValue !== newValue) {
        comparisons.push({
          field: label,
          oldValue,
          newValue,
        });
      }
    });

    return comparisons;
  };

  // Process and group `contractWorkPlaceItems`
  const groupContractWorkPlaceItems = (
    record: Contract | null,
  ): { workPlace: string; items: { deviceTypeName: string; quantity: number }[] }[] => {
    if (!record || !record.contractWorkPlaceItems) return [];

    // Group items by workPlace
    const groupedItems: Record<string, { deviceTypeName: string; quantity: number }[]> =
      record.contractWorkPlaceItems.reduce(
        (acc: any, item: any) => {
          const workPlaceName = item.workPlace?.name || 'Punct de lucru necunoscut';
          const deviceTypeName = item.device?.name || 'Device necunoscut';
          const quantity = item.piece || 0;

          if (!acc[workPlaceName]) {
            acc[workPlaceName] = [];
          }

          acc[workPlaceName].push({
            deviceTypeName,
            quantity,
          });

          return acc;
        },
        {} as Record<string, { deviceTypeName: string; quantity: number }[]>,
      );

    // Convert grouped items to an array of objects
    return Object.entries(groupedItems).map(([workPlace, items]) => ({
      workPlace,
      items,
    }));
  };

  const changes = compareSpecificFields(selectedRecord, selectedRecordAdditionalAct);
  const leftGroupedItems = groupContractWorkPlaceItems(selectedRecord);
  const rightGroupedItems = groupContractWorkPlaceItems(selectedRecordAdditionalAct);

  // Table columns for comparisons
  const changeColumns = [
    {
      title: 'Proprietate',
      dataIndex: 'field',
      key: 'field',
    },
    {
      title: 'Valoare curenta contract',
      dataIndex: 'oldValue',
      key: 'oldValue',
    },
    {
      title: 'Valoare curenta act aditional',
      dataIndex: 'newValue',
      key: 'newValue',
    },
  ];

  // Table columns for grouped items
  const groupedItemsColumns = [
    {
      title: 'Device',
      dataIndex: 'deviceTypeName',
      key: 'deviceTypeName',
    },
    {
      title: 'Cantitate',
      dataIndex: 'quantity',
      key: 'quantity',
    },
  ];

  const contractTableColumns = [
    { title: 'Camp', dataIndex: 'camp', key: 'camp', width: 160 },
    { title: 'Inainte', dataIndex: 'inainte', key: 'inainte' },
    { title: 'Dupa', dataIndex: 'dupa', key: 'dupa' },
  ];

  const equipmentTableColumns = [
    { title: 'Punct lucru - Periodicitate', dataIndex: 'camp', key: 'camp', width: 220 },
    {
      title: 'Inainte (sisteme)',
      dataIndex: 'inainte',
      key: 'inainte',
      render: (val: string, record: ContractUpdateRowDto) =>
        record.isChanged ? <span style={{ fontWeight: 500 }}>{val || '-'}</span> : val || '-',
    },
    {
      title: 'Dupa (sisteme)',
      dataIndex: 'dupa',
      key: 'dupa',
      render: (val: string, record: ContractUpdateRowDto) =>
        record.isChanged ? <span style={{ fontWeight: 500 }}>{val || '-'}</span> : val || '-',
    },
  ];

  return (
    <Drawer
      title='Informatii suplimentare'
      placement='right'
      onClose={handleModalInformationClose}
      open={modalInformation}
      width={isSmallScreen ? '95%' : '50%'}
    >
      <h2>Contract Nr. {selectedRecord?.number || 'N/A'} - Schimbari</h2>
      <Divider />

      {(contractUpdate || isUpdatesLoading) && (
        <>
          <h3>Istoric modificari (ultima modificare)</h3>
          {isUpdatesLoading ? (
            <Spin style={{ marginBottom: 16 }} />
          ) : contractUpdate ? (
            <>
              <p>
                <strong>Numar contract:</strong> {contractUpdate.contractNumber} &bull; <strong>Client:</strong>{' '}
                {contractUpdate.clientName}
              </p>
              <p>
                <strong>Modificat de:</strong> {contractUpdate.modifiedBy} &bull; <strong>La:</strong>{' '}
                {contractUpdate.modifiedWhen}
              </p>

              <Card size='small' title='Date contract' style={{ marginBottom: 16 }}>
                <Table
                  bordered
                  pagination={false}
                  size='small'
                  columns={contractTableColumns}
                  dataSource={(contractUpdate.rows || []).map((r, i) => ({ ...r, key: 'row-' + i }))}
                  rowClassName={(record) => (record.isChanged ? 'contract-update-row-changed' : '')}
                />
              </Card>

              {(contractUpdate.workPlaceGroupedRows?.length ?? 0) > 0 && (
                <Card
                  size='small'
                  title={
                    <span>
                      Modificari echipamente
                      {(contractUpdate.workPlaceGroupedRows?.filter((r) => r.isChanged).length ?? 0) > 0 && (
                        <Tag color='orange' style={{ marginLeft: 8 }}>
                          {contractUpdate.workPlaceGroupedRows?.filter((r) => r.isChanged).length ?? 0} modificate
                        </Tag>
                      )}
                    </span>
                  }
                  style={{ marginBottom: 16 }}
                >
                  <p style={{ color: '#666', marginBottom: 12, fontSize: 12 }}>
                    Sisteme per punct lucru și periodicitate. Rândurile evidențiate au fost modificate.
                  </p>
                  <Table
                    bordered
                    pagination={false}
                    size='small'
                    columns={equipmentTableColumns}
                    dataSource={(contractUpdate.workPlaceGroupedRows || [])
                      .map((r, i) => ({ ...r, key: 'wp-' + i }))
                      .sort((a, b) => (a.isChanged === b.isChanged ? 0 : a.isChanged ? -1 : 1))}
                    rowClassName={(record) => (record.isChanged ? 'contract-update-row-changed' : '')}
                  />
                </Card>
              )}
            </>
          ) : (
            <p style={{ color: '#666' }}>Nu exista modificari salvate pentru acest contract.</p>
          )}
          <Divider />
        </>
      )}

      {/* <Table
        bordered
        pagination={false}
        columns={changeColumns}
        dataSource={changes}
        rowKey={(record) => record.field}
        style={{ marginBottom: '16px' }}
      />

      <Divider />

      <h3>Device-uri</h3>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
      
        <div style={{ flex: 1 }}>
          <h4>Contractul Acual ({selectedRecord?.contractWorkPlaceItems?.length})</h4>
          {leftGroupedItems.map((group) => (
            <div key={group.workPlace} style={{ marginBottom: '16px' }}>
              <h5>Punct de lucru: {group.workPlace}</h5>
              <Table
                bordered
                pagination={false}
                columns={groupedItemsColumns}
                dataSource={group.items}
                rowKey={(record) => record.deviceTypeName}
              />
            </div>
          ))}
        </div>

        <div style={{ flex: 1 }}>
          <h4>Act Additional ({selectedRecordAdditionalAct?.contractWorkPlaceItems?.length})</h4>
          {rightGroupedItems.map((group) => (
            <div key={group.workPlace} style={{ marginBottom: '16px' }}>
              <h5>Punct de lucru: {group.workPlace}</h5>
              <Table
                bordered
                pagination={false}
                columns={groupedItemsColumns}
                dataSource={group.items}
                rowKey={(record) => record.deviceTypeName}
              />
            </div>
          ))}
        </div>
      </div> */}
    </Drawer>
  );
};

export default ContractInformation;
